import React, { useMemo, useState } from "react";

import {
  ALERT_TYPE_OPTIONS,
  AlertRuleDTO,
  TARGET_TYPE_OPTIONS,
  TRIGGER_LOGIC_OPTIONS,
} from "api/model/alerts/alert-types";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { T } from "services/lang/LanguageService";
import { timeAgo } from "utils/time-utils";
import EnabledToggle from "./components/EnabledToggle";
import PriorityTag from "./components/PriorityTag";
import RuleActions from "./components/RuleActions";
import TableHeader from "./components/TableHeader";

type AlertRulesTableProps = {
  rules: AlertRuleDTO[] | undefined;
  isLoading: boolean;
  onEdit: (rule: AlertRuleDTO) => void;
  onCreateClick: () => void;
};

const AlertRulesTable: React.FC<AlertRulesTableProps> = ({ rules, isLoading, onEdit, onCreateClick }) => {
  const [search, setSearch] = useState("");

  const filteredRules = useMemo(() => {
    if (!rules) return [];
    if (!search) return rules;
    return rules.filter(rule => rule.name.toLowerCase().includes(search.toLowerCase()));
  }, [rules, search]);

  const columns = useMemo(() => getColumns(onEdit), [onEdit]);

  return (
    <DataTable
      columns={columns}
      data={filteredRules}
      tableConfig={{
        loading: isLoading,
        scrollable: true,
        scrollHeight: "60vh",
        paginator: true,
        rows: 10,
        rowsPerPageOptions: [10, 25, 50],
        header: <TableHeader search={search} setSearch={setSearch} onCreateClick={onCreateClick} />,
        emptyMessage: T("alerts.no-rules"),
      }}
    />
  );
};

const getColumns = (onEdit: (rule: AlertRuleDTO) => void): TableColumn<AlertRuleDTO>[] => [
  {
    field: "id",
    header: "ID",
    columnKey: "id",
    sortable: true,
    className: "w-4rem",
  },
  {
    field: "name",
    header: T("word.name"),
    columnKey: "name",
    sortable: true,
  },
  {
    field: "enabled",
    header: T("word.enabled"),
    columnKey: "enabled",
    body: row => <EnabledToggle rule={row} />,
    className: "w-6rem",
  },
  {
    field: "alertType",
    header: T("word.type"),
    columnKey: "alertType",
    body: row => ALERT_TYPE_OPTIONS.find(o => o.value === row.alertType)?.label,
  },
  {
    field: "priority",
    header: T("word.priority"),
    columnKey: "priority",
    body: row => <PriorityTag priority={row.priority} />,
  },
  {
    field: "targetType",
    header: T("word.target"),
    columnKey: "targetType",
    body: row => TARGET_TYPE_OPTIONS.find(o => o.value === row.targetType)?.label,
  },
  {
    field: "triggerLogic",
    header: T("alerts.trigger-logic"),
    columnKey: "triggerLogic",
    body: row => TRIGGER_LOGIC_OPTIONS.find(o => o.value === row.triggerLogic)?.label,
  },
  {
    field: "triggers",
    header: T("alerts.triggers"),
    columnKey: "triggers",
    body: row => `${row.triggers.length} ${T("alerts.triggers").toLowerCase()}`,
  },
  {
    field: "updatedAt",
    header: T("word.updated"),
    columnKey: "updatedAt",
    body: row => timeAgo(row.updatedAt),
    sortable: true,
  },
  {
    header: T("word.actions"),
    columnKey: "actions",
    body: row => <RuleActions rule={row} onEdit={onEdit} />,
    className: "w-6rem",
  },
];

export default AlertRulesTable;
